import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  InputBase,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search,
  AccessTime,
  AppsOutlined,
  StarOutlineRounded,
  StarBorder,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import Sidebar from '../landingPage/Sidebar';
import ProjectsGrid from './ProjectsGrid';
import WorkflowTable from './WorkflowTable';
import NewProjectModal from './NewProjectModal';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchWorkflows,
  fetchWorkflowConfigAndSet,
  deleteWorkflow,
  clearWorkflow,
} from '../../state/slices/workflowSlice';
import { fetchProjects } from '../../state/slices/projectSlice';
import { updateSelectedEntitiesFromBackend } from '../../utils/EntityUtils';
import { removeAllEntitiesForWorkflow } from '../../state/slices/entitiesSlice';
import { deleteMappingForWorkflow } from '../../state/slices/mappingSlice';
import { verifyPasswordForReauth, verifyGoogleReauth, isGoogleUser } from '../../services/reauthService';

const SidebarLayout = () => {
  const [, setSelectedProject] = useState(null);
  const [activeNav, setActiveNav] = useState('Recent');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [starredProjectIds, setStarredProjectIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  
  // Reauthentication state
  const [step, setStep] = useState('confirm'); // 'confirm' -> 'reauth' -> 'verify'
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const borderBase = 'rgba(79, 172, 254, 0.1)';

  const { list: workflows = [], loading: workflowLoading } = useSelector(
    (state) => state.workflows
  );
  const { list: projects = [], loading: projectLoading } = useSelector(
    (state) => state.projects
  );
  const projectsGridRef = useRef();
  const starredWorkflows = useMemo(
    () => workflows.filter((wf) => Boolean(wf?.starred || wf?.is_starred)),
    [workflows]
  );
  const filteredStarredWorkflows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return starredWorkflows;
    return starredWorkflows.filter((wf) => {
      const name = (wf?.name || wf?.workflow_name || '').toLowerCase();
      const description = (wf?.description || '').toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }, [starredWorkflows, searchTerm]);
  
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isDeleteDialogOpen) {
      setStep('confirm');
      setPassword('');
      setShowPassword(false);
      setReauthError('');
      setReauthLoading(false);
    }
  }, [isDeleteDialogOpen]);
  
  const handleDashboardProjectSelect = (project) => {
    setSelectedProject(project);
    setTimeout(() => {
      projectsGridRef.current?.scrollToTable();
    }, 100);
  };

  useEffect(() => {
    if (workflows.length !== 0) {
      dispatch(fetchWorkflows())
        .unwrap()
        .catch(() => toast.error('Failed to fetch workflows.'));
    }
  }, [dispatch, workflows.length]);

  useEffect(() => {
    dispatch(fetchProjects())
      .unwrap()
      .then((projects) => {
        // Initialize starred projects from backend data
        const starredIds = projects
          .filter(project => project.is_starred)
          .map(project => project.project_id);
        setStarredProjectIds(starredIds);
      })
      .catch(() => toast.error('Failed to fetch projects.'));
  }, [dispatch]);

  const handleNavigationClick = (navItem) => setActiveNav(navItem);

  // const handleEdit = (id) => {
  //   const workflow = workflows.find(flow => flow.workflow_id === id);
  //   if (workflow) {
  //     navigate(`/parentworkflow/${workflow.name}`);
  //   } else {
  //     toast.error('Workflow not found!');
  //   }
  // };
  const handleEdit = async (workflowId) => {
    try {
      const workflow = workflows.find(
        (flow) => flow.workflow_id === workflowId
      );
      if (!workflow) {
        toast.error('Workflow not found!');
        return;
      }

      // const workflowName = workflow.name;
      const workflowName = workflow.workflow_id;
      try {
        const result = await dispatch(
          fetchWorkflowConfigAndSet({
            workflowId,
            workflowName,
          })
        ).unwrap();

        if (result?.entitySelections) {
          updateSelectedEntitiesFromBackend(
            dispatch,
            workflowId,
            result.entitySelections
          );
        }
        console.log('✅ Config received from backend:', result);
      } catch (error) {
        console.error('Error loading workflow configuration:', error);
      }

      navigate(`/parentworkflow/${workflow.workflow_id}`);
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toast.error('An error occurred while editing the workflow');
    }
  };

  const handleDelete = (workflowId) => {
    setWorkflowToDelete(workflowId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteClick = () => {
    if (isGoogleUser()) {
      handleGoogleReauth();
    } else {
      setStep('reauth');
    }
  };

  const handleGoogleReauth = async () => {
    try {
      setReauthLoading(true);
      setReauthError('');
      const result = await verifyGoogleReauth();
      if (result.success) {
        await performDelete();
      } else {
        setReauthError(result.error || 'Reauthentication failed');
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const handlePasswordReauth = async () => {
    if (!password.trim()) {
      setReauthError('Please enter your password');
      return;
    }

    try {
      setReauthLoading(true);
      setReauthError('');
      const result = await verifyPasswordForReauth(password);
      if (result.success) {
        await performDelete();
      } else {
        setReauthError(result.error || 'Wrong password.');
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const performDelete = async () => {
    try {
      if (!workflowToDelete) return;

      const workflow = workflows.find(
        (flow) => flow.workflow_id === workflowToDelete
      );
      if (!workflow) {
        try {
          toast.error('Workflow not found!');
        } catch (toastError) {
          console.error('Toast error:', toastError);
        }
        return;
      }

      const workflowId = workflow.workflow_id;
      // const workflowName = workflow.name;

      // Dispatch delete action
      await dispatch(deleteWorkflow(workflowToDelete)).unwrap();
      dispatch(clearWorkflow(workflowId));
      dispatch(removeAllEntitiesForWorkflow(workflowId));
      dispatch(deleteMappingForWorkflow(workflowId));

      // Show success message
      try {
        toast.success('Workflow deleted successfully', { autoClose: 2000 });
      } catch (toastError) {
        console.error('Toast error:', toastError);
      }

      // Refresh workflows list
      setTimeout(() => {
        dispatch(fetchWorkflows());
      }, 2200);
    } catch (error) {
      console.error('Error deleting workflow:', error);
      try {
        toast.error(error.message || 'Failed to delete workflow');
      } catch (toastError) {
        console.error('Toast error:', toastError);
      }
    } finally {
      setIsDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };



  const renderMainContent = () => {
    switch (activeNav) {
      case 'Recent':
        return (
          <>
            <ProjectsGrid
              ref={projectsGridRef}
              onProjectSelect={handleDashboardProjectSelect}
              limitProjectsToFive={true}
              workflowLoading={workflowLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              starredProjectIds={starredProjectIds}
              setStarredProjectIds={setStarredProjectIds}
              showOnlyStarred={activeNav === 'Starred Projects'}
              showTableOnLoad={true}
              searchTerm={searchTerm}
            />
          </>
        );
      case 'All Projects':
        return (
          <>
            {/* <ProjectsGrid onProjectSelect={setSelectedProject} />; */}
            <ProjectsGrid
              ref={projectsGridRef}
              onProjectSelect={setSelectedProject}
              workflowLoading={workflowLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              starredProjectIds={starredProjectIds}
              setStarredProjectIds={setStarredProjectIds}
              showOnlyStarred={activeNav === 'Starred Projects'}
              searchTerm={searchTerm}
            />
          </>
        );
      case 'Starred Projects':
        return (
          <ProjectsGrid
            ref={projectsGridRef}
            onProjectSelect={setSelectedProject}
            workflowLoading={workflowLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            starredProjectIds={starredProjectIds}
            setStarredProjectIds={setStarredProjectIds}
            // showOnlyStarred={activeNav === 'Starred Projects'}
            showOnlyStarred={true}
            searchTerm={searchTerm}
          />
        );
      case 'Starred Workflows':
        return (
          <WorkflowTable
            title="Starred Workflows"
            workflows={filteredStarredWorkflows}
            loading={workflowLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      default:
        return <div>Select a project category from the sidebar</div>;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >

      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          bgcolor: '#F7F9FF',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                width: { xs: '100%', md: '100%' },
                borderRadius: '6px',
                boxShadow: '0px 4px 10px 0px #0000000D',
                background: '#fff',
                color: '#A0A0A0',
                border: '1px solid #0000000D',
              }}
            >
              <Search sx={{ color: '#A0A0A0' }} />
              <InputBase
                placeholder="Type to Search..."
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  ml: 1,
                  fontSize: '0.875rem',
                  color: '#464343ff',
                  background: 'transparent',
                  '& input': {
                    padding: '4px 0',
                  },
                }}
              />
            </Paper>
            <Box>
              <NewProjectModal />
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                display: 'inline-flex',
                gap: '16px',
                backgroundColor: 'transparent',
                borderRadius: '6px',
                py: '4px',
                px: '6px',
              }}
            >
              {['Recent', 'All Projects', 'Starred Projects', 'Starred Workflows'].map((item) => {
                const isActive = activeNav === item;
                const getIcon = () => {
                  if (item === 'Recent')
                    return (
                      <AccessTime
                        sx={{ fontSize: '16px', color: isActive ? '#ffffff' : '#555555' }}
                      />
                    );
                  if (item === 'All Projects')
                    return (
                      <AppsOutlined
                        sx={{ fontSize: '16px', color: isActive ? '#ffffff' : '#555555' }}
                      />
                    );
                  if (item === 'Starred Projects')
                    return (
                      <StarOutlineRounded
                        sx={{ fontSize: '16px', color: isActive ? '#ffffff' : '#555555' }}
                      />
                    );
                  if (item === 'Starred Workflows')
                    return (
                      <StarBorder
                        sx={{ fontSize: '16px', color: isActive ? '#ffffff' : '#555555' }}
                      />
                    );
                };
                const displayLabel =
                  item === 'Starred Projects'
                    ? 'Starred Project'
                    : item.replace(' Projects', '');
                return (
                  <Button
                    key={item}
                    onClick={() => handleNavigationClick(item)}
                    startIcon={getIcon()}
                    disableRipple
                    sx={{
                      px: 2.5,
                      py: 0.7,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      borderRadius: '8px',
                      minWidth: 'auto',
                      backgroundColor: isActive ? '#5A66A6' : '#EAF0FF',
                      color: isActive ? '#ffffff' : '#555555',
                      border: 'none',
                      boxShadow: '0 6px 10px rgba(0,0,0,0.10)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isActive ? '#4C5B9A' : '#D6E4FF',
                        color: isActive ? '#ffffff' : '#5A66A6',
                      },
                    }}
                  >
                    {displayLabel}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>
        {projectLoading ? (
          <Box sx={{ mt: 7, textAlign: 'center' }}>
            <CircularProgress size={50} />
          </Box>
        ) : projects.length === 0 && activeNav !== 'Starred Workflows' ? (
          <Box
            sx={{
              textAlign: 'center',
              mt: 5,
              color: '#999',
              fontSize: '1rem',
              fontWeight: 500,
            }}
          >
            <Typography variant="h6">
              You haven’t created any projects yet. Click{' '}
              <Box component="span" sx={{ fontWeight: 600, color: '#264FDA' }}>
                “Create New Project”
              </Box>{' '}
              to get started!
            </Typography>
          </Box>
        ) : (
          renderMainContent()
        )}
      </Box>

      <Dialog
  open={isDeleteDialogOpen}
  onClose={() => {
    setIsDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  }}
  keepMounted
  fullWidth
  // You can keep maxWidth if you want, but the Paper width below takes precedence
  // maxWidth="sm"
  PaperProps={{
    elevation: 0,
    sx: {
      borderRadius: 1,
      width: 520,
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      border: `1px solid ${borderBase}`,
      boxShadow: '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
      backdropFilter: 'blur(4px)',
    },
  }}
  BackdropProps={{
    sx: {
      backgroundColor:'rgba(10, 18, 30, 0.35)',
      backdropFilter: 'blur(4px)',
      '& .MuiDialog-container': { alignItems: 'center' },
    },
  }}
  transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
>
  <DialogTitle sx={{ pb: 1,textAlign: 'center', }}>
    {step === 'confirm' ? 'Delete Workflow?' : 'Reauthenticate to Continue'}
  </DialogTitle>

  <DialogContent
    sx={{
      // match the first dialog's spacing & layout
      py: 5,
      px: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      textAlign: 'center',
    }}
  >
    {step === 'confirm' ? (
      <>
        <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
          Are you sure you want to delete this workflow? This action cannot be undone.
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          You will need to reauthenticate to confirm this action.
        </Typography>
      </>
    ) : (
      <>
        <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
          Please enter your password to confirm deletion of this workflow.
        </Typography>

        <Box sx={{ width: '100%', maxWidth: 300 }}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            error={!!reauthError}
            helperText={reauthError}
            disabled={reauthLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={reauthLoading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: '#f8fafc',
                '& fieldset': {
                  borderColor: reauthError ? '#ef4444' : borderBase,
                },
                '&:hover fieldset': {
                  borderColor: reauthError ? '#ef4444' : 'rgba(79, 172, 254, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: reauthError ? '#ef4444' : '#4facfe',
                },
              },
            }}
          />
        </Box>
      </>
    )}
  </DialogContent>

  <DialogActions sx={{ p: 3, pt: 1 }}>
    {step === 'confirm' ? (
      <>
        <Button
          onClick={() => {
            setIsDeleteDialogOpen(false);
            setWorkflowToDelete(null);
          }}
          variant="text"
          sx={{
            minWidth: 100,
            height: 40,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            color:'#5965A6'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteClick}
          variant="contained"
          disabled={reauthLoading}
          startIcon={reauthLoading ? <CircularProgress size={16} sx={{'& muiCircularProgress-svg': {color: '#fff'}}}/> : null}
          sx={{
            minWidth: 160,
            height: 40,
            borderRadius: 1,
            px: 4,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #de1d0f, #f87f5d)',
            color: '#fff',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #e61405, #de1d0f)',
              boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
            transition: 'all .18s ease',
            opacity: reauthLoading ? 0.85 : 1,
          }}
        >
          {reauthLoading ? 'Verifying…' : 'Delete Workflow'}
        </Button>
      </>
    ) : (
      <>
        <Button
          onClick={() => setStep('confirm')}
          variant="text"
          disabled={reauthLoading}
          sx={{
            minWidth: 100,
            height: 40,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            color:'#5965A6'
          }}
        >
          Back
        </Button>
        <Button
          onClick={handlePasswordReauth}
          variant="contained"
          disabled={reauthLoading || !password.trim()}
          startIcon={reauthLoading ? <CircularProgress size={16} /> : null}
          sx={{
            minWidth: 160,
            height: 40,
            borderRadius: 1,
            px: 4,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #de1d0f, #f87f5d)',
            color: '#fff',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #e61405, #de1d0f)',
              boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
            transition: 'all .18s ease',
            opacity: reauthLoading || !password.trim() ? 0.85 : 1,
          }}
        >
          {reauthLoading ? 'Verifying…' : 'Verify Password'}
        </Button>
      </>
    )}
  </DialogActions>
</Dialog>

    </Box>
  );
};

export default SidebarLayout;